import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import axios from 'axios';
import useModalStore from '../../../zustand/store/useModalStore';
import useConfirm from '../../../hooks/useConfirm';

import '../../../css/data-grid.css';

/**
 * @정보관리요청내역
 * 월별 모니터링 하단의 'NEDIS 정보관리 요청내역'(요청내역 + 댓글) 영역.
    <FidelityMgmtMsg
        ref={mgmtMsgRef}                // ref.search() 로 조회 실행
        searchParams={searchParams}     // 검색 조건(searchEmog, fromindt) - search() 실행 시점에만 읽음
    />
 * **/
const FidelityMgmtMsg = forwardRef(({ searchParams }, ref) => {

    const userId = localStorage.getItem('id');
    const userName = localStorage.getItem('name');
    const useremcd = localStorage.getItem('useremcd');
    const userAuth = localStorage.getItem('userAuth');
    const admin = localStorage.getItem('superAdmin') === 'true' || userAuth === 'J002';

    const confirm = useConfirm();

    // toast modal open function
    const showNotiModal = useModalStore(state => state.showNotiModal);

    const [isSearched, setIsSearched] = useState(false);
    const [mgmtMsgData, setMgmtMsgData] = useState({});
    const [mgmtCmtData, setMgmtCmtData] = useState({});

    // 조회 시점에 확정된 검색조건 state (대상기관 / 내원기간)
    const [searchedCond, setSearchedCond] = useState(null);

    // 정보관리 요청내역(댓글) 조회 - 조회 시점에 확정된 조건으로만 호출
    const getEvlInfoMgmtMsgData = async (cond) => {
        try {
            const response = await axios.get('/ptmiapi/fidelity_mgmt_msg_data.do', {
                params: {
                    evlInEmog: useremcd,
                    evlTgtEmog: cond.evlTgtEmog,
                    evlInYyyymm: cond.evlInYyyymm
                }
            });
            const response2 = await axios.get('/ptmiapi/fidelity_msg_cmt_data.do', {
                params: {
                    evlTgtEmog: cond.evlTgtEmog,
                    evlInYyyymm: cond.evlInYyyymm
                }
            });

            setIsSearched(true);
            setMgmtMsgData(response.data);
            setMgmtCmtData(response2.data);

        } catch (e) {
            console.error('정보관리 요청내역 로딩 중 오류 발생', e);
        }
    }

    // 등록/수정/삭제 후 재조회 - 조회 시점 조건 그대로 사용
    const reloadMgmtMsgData = () => getEvlInfoMgmtMsgData(searchedCond);

    // 부모(모니터링)에서 조회를 실행하기 위한 handle (검색 버튼을 누른 시점의 조건을 저장해두고 이후 등록/수정/삭제에 사용)
    useImperativeHandle(ref, () => ({
        search: async () => {
            const cond = {
                evlTgtEmog: searchParams.searchEmog,
                evlInYyyymm: searchParams.fromindt.substring(0, 6)
            };
            setSearchedCond(cond);
            await getEvlInfoMgmtMsgData(cond);
        }
    }));

    // 요청내역 수정 내용
    const [mgntText, setMgmtText] = useState('');
    // 요청내역 수정 상태 관리 (수정 중인 행의 key — 행이 여러 개일 때 수정 모드가 섞이지 않도록 행 단위로 관리)
    const [editingKey, setEditingKey] = useState(null);
    // 요청내역 수정 시 포커스를 위한 요청내역 ref
    const textAreaRef = useRef(null);

    // 요청내역 수정 버튼 클릭
    const handleMgmtEdit = (rowKey, currentValue) => {
        if (editingKey !== rowKey) {
            // 초기값 세팅
            setMgmtText(currentValue);
            // 포커스
            setEditingKey(rowKey);
            setTimeout(() => {
                if (textAreaRef.current) {
                    textAreaRef.current.focus();
                    // 커서 맨 뒤로 보내기
                    const length = textAreaRef.current.value.length;
                    textAreaRef.current.setSelectionRange(length, length);
                }
            }, 0);
        } else {
            setEditingKey(null);
        }
    };

    // 요청내역 등록
    const mgmtAdd = async () => {
        try {
            const ok = await confirm('정보관리 요청 내역을 등록하시겠습니까?');
            if (!ok) return false;

            if (mgntText === null || mgntText === undefined || mgntText === '') {
                showNotiModal('요청 내용은 필수입니다.');
                return false;
            };

            const response = await axios.post('/ptmiapi/mgmt_register.do', {
                evlTgtEmog: searchedCond.evlTgtEmog,
                evlInYyyymm: searchedCond.evlInYyyymm,
                evlInfoCot: mgntText
            });

            if (response) {
                showNotiModal(response.data.resultMessage);
                reloadMgmtMsgData();
                setMgmtText('');
            }

        } catch (e) {
            console.error('정보관리 요청 내역 등록 중 오류 발생', e);
        }
    };

    // 요청내역 수정
    const mgmtModify = async (EVL_IN_EMOG, EVL_TGT_EMOG, EVL_IN_YYYYMM) => {
        try {
            if (mgntText === null || mgntText === undefined || mgntText === '') {
                showNotiModal('요청 내용은 필수입니다.');
                return false;
            };

            const ok = await confirm('정보관리 요청 내역을 수정하시겠습니까?');
            if (!ok) return false;

            const response = await axios.post('/ptmiapi/mgmt_update.do', {
                evlInEmog: EVL_IN_EMOG,
                evlTgtEmog: EVL_TGT_EMOG,
                evlInYyyymm: EVL_IN_YYYYMM,
                evlInfoCot: mgntText
            });

            if (response) {
                reloadMgmtMsgData();

                // 수정 상태 해제
                setEditingKey(null);
                showNotiModal('정보관리 요청 내역이 정상적으로 수정되었습니다.');
            }

        } catch (e) {
            console.error('정보관리 요청 내역 수정 중 오류 발생', e);
        }
    };

    // 댓글 상태 관리
    const [cmtCot, setCmtCot] = useState('');
    // 수정 중인 댓글 cmt id
    const [editingIds, setEditingIds] = useState(new Set());
    // 댓글 id별 textarea 값 관리
    const [editValues, setEditValues] = useState({});
    // 댓글 수정 클릭 시 댓글 id ref
    const textareaRefs = useRef({});

    // 댓글 수정 버튼 클릭
    const handleEdit = (id, currentValue) => {
        setEditingIds(prev => {
            const newSet = new Set(prev);
            newSet.add(id);
            return newSet;
        });
        // 초기값 세팅
        setEditValues(prev => ({
            ...prev,
            [id]: currentValue
        }));
        // 수정 댓글 포커스
        setTimeout(() => {
            const el = textareaRefs.current[id];
            el?.focus();
            el?.setSelectionRange(el.value.length, el.value.length);
        }, 0);
    };

    // 댓글 수정 창 onChange
    const handleChange = (id, value) => {
        setEditValues(prev => ({
            ...prev,
            [id]: value
        }));
    };

    // 댓글 등록
    const cmtAdd = async (EVL_IN_EMOG) => {
        try {
            const ok = await confirm('댓글을 등록하시겠습니까?');
            if (!ok) return false;

            if (cmtCot === null || cmtCot === undefined || cmtCot === '') {
                showNotiModal('댓글 내용은 필수입니다.');
                return false;
            };

            const response = await axios.post('/ptmiapi/mgmt_cmt_register.do', {
                evlInEmog: EVL_IN_EMOG,
                evlTgtEmog: searchedCond.evlTgtEmog,
                evlInYyyymm: searchedCond.evlInYyyymm,
                evlCmtCot: cmtCot
            });

            if (response) {
                showNotiModal(response.data.resultMessage);
                reloadMgmtMsgData();
                setCmtCot('');
            }

        } catch (e) {
            console.error('정보관리 요청 내역 등록 중 오류 발생', e);
        }
    };

    // 댓글 수정
    const cmtModify = async (cmtSno) => {
        try {
            const ok = await confirm('댓글을 수정하시겠습니까?');
            if (!ok) return false;

            const newValue = editValues[cmtSno];

            const response = await axios.post('/ptmiapi/mgmt_cmt_update.do', {
                evlCmtSno: cmtSno,
                evlCmtCot: newValue
            });

            if (response) {
                // 해당 댓글만 다시 조회
                const res = await axios.get('/ptmiapi/fidelity_msg_specific_cmt.do', {
                    params: {
                        evlCmtSno: cmtSno
                    }
                });

                const updatedComment = res.data;

                setMgmtCmtData(prev => prev.map(item => (
                    item.EVL_CMT_SNO === cmtSno ? updatedComment : item
                )));

                // 수정 상태 해제
                setEditingIds(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(cmtSno);
                    return newSet;
                });
                showNotiModal('댓글이 정상적으로 수정되었습니다.');
            }

        } catch (e) {
            showNotiModal('댓글 수정 중 오류가 발생되었습니다.');
            console.error('comment 수정 중 오류 발생', e);
        }
    };

    // 댓글 수정 취소
    const handleCancel = (id) => {
        setEditingIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(id);
            return newSet;
        });
    };

    // 댓글 삭제
    const cmtDelete = async (cmtSno) => {
        try {
            const ok = await confirm('댓글을 삭제하시겠습니까?');
            if (!ok) return false;

            const response = await axios.post('/ptmiapi/mgmt_cmt_delete.do', {
                evlCmtSno: cmtSno
            });

            if (response) {
                showNotiModal('삭제가 완료되었습니다.');
                reloadMgmtMsgData();
            }

        } catch (e) {
            showNotiModal('댓글 삭제 중 오류가 발생되었습니다.');
            console.error('comment 삭제 중 오류 발생', e);
        }
    };

    // textarea 글자수 유효성검사
    const MAX_BYTE = 4000;

    const getByteLength = (str) => {
        let byte = 0;
        for (let i = 0; i < str.length; i++) {
            // 아스키코드 128 초과(한글, 한자 등)는 2byte, 그 외는 1byte
            byte += str.charCodeAt(i) > 128 ? 2 : 1;
        }
        return byte;
    };

    // 요청내역 및 댓글 등록 창 onChange
    const handleTextInputChange = (e) => {
        const { name, value } = e.target;
        if (name === 'cmtCot') {
            setCmtCot(value);
        }
        if (name === 'mgntText') {
            if (getByteLength(value) <= MAX_BYTE) {
                setMgmtText(value);
            } else {
                // 4000바이트를 넘기려고 할 때 초과 부분 자르기 (복붙 방지)
                let currentByte = 0;
                let cutIndex = 0;

                for (let i = 0; i < value.length; i++) {
                    const charByte = value.charCodeAt(i) > 128 ? 2 : 1;
                    if (currentByte + charByte > MAX_BYTE) break;
                    currentByte += charByte;
                    cutIndex = i + 1;
                }

                setMgmtText(value.substring(0, cutIndex));
            }
        }
    };

    if (!isSearched) return null;

    return (
        <>
            <table>
                <colgroup>
                    {Array.from({ length: 9 }).map((_, idx) => (
                        <col key={idx} width={`${100 / 9}%`} />
                    ))}
                </colgroup>
                <tbody>
                    <tr className='table_title'>
                        <td colSpan={9}>
                            <div className='search_btn_wrapper'>
                                <h5 className='sub_title m-0'>▶ NEDIS 정보관리 요청내역</h5>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
            <table className='mgmt_msg_grid'>
                <colgroup>
                    <col width='8%' />
                    <col width='14%' />
                    <col width='10%' />
                    <col width='14%' />
                    <col />
                    <col width='7%' />
                </colgroup>
                <thead>
                    <tr className='colored bold'>
                        <th>작성자</th>
                        <th>작성일시</th>
                        <th>최초조회자</th>
                        <th>조회일시</th>
                        <th colSpan={mgmtMsgData.length > 0 && admin ? 1 : 2}>정보관리 요청내역</th>
                        {mgmtMsgData.length > 0 && admin ? <th>수정</th> : ''}
                    </tr>
                </thead>
                <tbody>
                    {mgmtMsgData.length > 0 ?
                        // NEDIS 정보관리 요청내역이 있을 때
                        mgmtMsgData.map((item, idx) => {
                            const rowKey = `${item.EVL_IN_EMOG}_${item.EVL_TGT_EMOG}_${item.EVL_IN_YYYYMM}`;
                            const isEditingRow = editingKey === rowKey;
                            return (
                                <tr key={idx}>
                                    <td>{item.EVL_IN_NAM}</td>
                                    <td>{item.EVL_UP_DTM ?? item.EVL_WR_DTM}</td>
                                    <td>{item.EVL_RD_NAM}</td>
                                    <td>{item.EVL_RD_DTM}</td>
                                    <td colSpan={admin ? '1' : '2'}>
                                        {admin ?
                                            (!isEditingRow ? <div>{item.EVL_INFO_COT}</div> :
                                                <div className='m-2'>
                                                    <textarea
                                                        id='mgntText'
                                                        name='mgntText'
                                                        className='border rounded w-full p-2'
                                                        rows={3}
                                                        ref={textAreaRef}
                                                        value={mgntText ?? ''}
                                                        onChange={handleTextInputChange}
                                                    />
                                                    <div className='flex justify-end gap-2 mt-2'>
                                                        <span><span className='tr_dark'>{getByteLength(mgntText)}</span> / 4000</span>
                                                        <button className='pw_btn bg_st_primary' onClick={() => mgmtModify(item.EVL_IN_EMOG, item.EVL_TGT_EMOG, item.EVL_IN_YYYYMM)}>수정</button>
                                                    </div>
                                                </div>
                                            )
                                            :
                                            <div>{item.EVL_INFO_COT}</div>
                                        }
                                    </td>
                                    {admin &&
                                        <td>
                                            <button className={`pw_btn m-auto ${isEditingRow ? 'bg_co_black' : 'bg_st_primary'}`}
                                                onClick={() => handleMgmtEdit(rowKey, item.EVL_INFO_COT)}>
                                                {isEditingRow ? '취소' : '수정'}</button>
                                        </td>
                                    }
                                </tr>
                            )
                        })
                        :
                        // NEDIS 정보관리 요청내역이 없을 때
                        <tr>
                            <td>{userName}</td>
                            <td>-</td>
                            <td>-</td>
                            <td>-</td>
                            <td colSpan={2}>
                                {admin ?
                                    <div className='m-2'>
                                        <textarea
                                            id='mgntText'
                                            name='mgntText'
                                            className='border rounded w-full p-2'
                                            rows={3}
                                            value={mgntText ?? ''}
                                            onChange={handleTextInputChange}
                                        />
                                        <div className='flex justify-end gap-2 mt-2'>
                                            <span><span className='tr_dark'>{getByteLength(mgntText)}</span> / 4000</span>
                                            <button onClick={() => mgmtAdd()} className='pw_btn bg_st_primary' disabled={mgntText === ''}>입력</button>
                                        </div>
                                    </div>
                                    :
                                    <div></div>
                                }
                            </td>
                        </tr>
                    }
                </tbody>
            </table>
            {/* 댓글(정보관리 요청내역이 있을 때만 표출) */}
            {mgmtMsgData.length < 1 ? '' :
                <div style={{ width: '99.7%' }}>
                    <div className={`bg_st_odd ${mgmtCmtData.length > 0 && 'mb-2'}`}>
                        {mgmtCmtData.length > 0 && mgmtCmtData?.map((item) => {
                            const myCmt = item.EVL_CMT_RES_ID === userId;
                            return (
                                <div key={item.EVL_CMT_SNO}>
                                    <div className='flex flex-col p-4 gap-2'>
                                        <div className='flex justify-between'>
                                            <span className='bold'>
                                                {item.EVL_CMT_USR_NAM} [{item.EMOGDESC}]
                                                <span className='co_gray ml-3'>{item.EVL_CMT_RES_DTM}</span>
                                            </span>
                                            {(admin || myCmt) && !editingIds.has(item.EVL_CMT_SNO) &&
                                                <div className='flex gap-2'>
                                                    <button
                                                        className='cursor-pointer bold co_dark st_bright_color_h'
                                                        onClick={() => handleEdit(item.EVL_CMT_SNO, item.EVL_CMT_COT)}
                                                    >수정</button>
                                                    <button
                                                        className='cursor-pointer bold co_dark tr_bright_color_h'
                                                        onClick={() => cmtDelete(item.EVL_CMT_SNO)}
                                                    >삭제</button>
                                                </div>
                                            }
                                        </div>
                                        {editingIds.has(item.EVL_CMT_SNO) ?
                                            <div className='flex gap-2'>
                                                <textarea
                                                    id='cmtCot'
                                                    name='cmtCot'
                                                    ref={(el) => (textareaRefs.current[item.EVL_CMT_SNO] = el)}
                                                    className='border rounded w-full p-2'
                                                    value={editValues[item.EVL_CMT_SNO] ?? ''}
                                                    onChange={(e) => handleChange(item.EVL_CMT_SNO, e.target.value)}
                                                />
                                                <button
                                                    className='comm_btn bg_st_primary h-10 m-auto'
                                                    onClick={() => cmtModify(item.EVL_CMT_SNO)}
                                                    disabled={editValues[item.EVL_CMT_SNO] === ''}
                                                >수정
                                                </button>
                                                <button
                                                    className='comm_btn bg_co_black h-10 m-auto'
                                                    onClick={() => handleCancel(item.EVL_CMT_SNO)}
                                                >취소
                                                </button>
                                            </div>
                                            :
                                            <p className='mx-2'>
                                                {item.EVL_CMT_COT?.split('\n').map((line, idx) => (
                                                    <span key={idx}>
                                                        {line}
                                                        <br />
                                                    </span>
                                                ))}
                                            </p>
                                        }
                                    </div>
                                    {<hr className='wp-98 m-auto' />}
                                </div>
                            )
                        })}
                        {mgmtMsgData.length > 0 &&
                            <div className='flex gap-3 p-4'>
                                <span className='bold whitespace-no-wrap content-center'>{userName}</span>
                                <textarea
                                    id='cmtCot'
                                    name='cmtCot'
                                    className='border rounded w-full p-2'
                                    value={cmtCot}
                                    onChange={handleTextInputChange}
                                />
                                <button
                                    className='comm_btn bg_st_primary h-10 m-auto'
                                    onClick={() => cmtAdd(mgmtMsgData[0].EVL_IN_EMOG)}
                                    disabled={cmtCot === ''}
                                >등록
                                </button>
                            </div>
                        }
                    </div>
                </div>
            }
        </>
    )
});

export default FidelityMgmtMsg;
